//This File is used to create a notification when a incoming call is received, if the user is on a differnet page
import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import BASE_URL from '../../config';

const SipContext = createContext();
export const useSip = () => useContext(SipContext);

export const SipProvider = ({ children }) => {
  const [incomingCall, setIncomingCall] = useState(null);
  const [sipReady, setSipReady] = useState(false);
  const [sipClient, setSipClient] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [sessions, setSessions] = useState({});

  const [accountData, setAccountData] = useState(null);
  const token = localStorage.getItem('jwt_token');

  // Generate unique ID for sessions
  const getUniqueID = () => Math.random().toString(36).substr(2, 9);

  // Function to safely play audio
  const safePlayAudio = (audioElement) => {
    if (!audioElement) return;
    
    const playPromise = audioElement.play();
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.log('Audio playback prevented:', error);
        // If audio was prevented, we'll try to enable it on next user interaction
        setAudioEnabled(false);
      });
    }
  };

  // Enable audio on user interaction
  useEffect(() => {
    const enableAudio = () => {
      setAudioEnabled(true);
      // Remove the event listener after first interaction
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };

    document.addEventListener('click', enableAudio);
    document.addEventListener('touchstart', enableAudio);

    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };
  }, []);

  const initSip = (creds) => {
    if (!window.SIP) {
      console.warn("SIP.js not loaded.");
      return;
    }

    const ctxSip = {
      config: {
        
        password: creds.Pass,
        authorizationUser: creds.User,
        displayName: creds.Display,
        uri: `${creds.User}@${creds.Realm}`,
        transportOptions: {
          wsServers: creds.WSServer,
          traceSip: true,
        },
        registerOptions: {
          expires: 180,
        },
        hackWssInTransport: true,
        log: { builtinEnabled: true, level: 3 },
      },
    };

    const ua = new window.SIP.UA(ctxSip.config);

    ua.on('invite', (incomingSession) => {
      incomingSession.direction = 'incoming';
      const ctxid = getUniqueID();
      incomingSession.ctxid = ctxid;

      setSessions(prev => ({ ...prev, [ctxid]: incomingSession }));

      setIncomingCall({
        session: incomingSession,
        displayName:
          incomingSession.remoteIdentity.displayName ||
          incomingSession.remoteIdentity.uri.user,
        ctxid: ctxid,
      });

      // Cleanup handlers
      incomingSession.on('terminated', () => {
        console.log('Incoming call terminated');
        setSessions(prev => { const u = {...prev}; delete u[ctxid]; return u; });
        setActiveSession(curr => curr?.ctxid === ctxid ? null : curr);
        setIncomingCall(null);
      });
      incomingSession.on('cancel', () => {
        console.log('Incoming call cancelled');
        setSessions(prev => { const u = {...prev}; delete u[ctxid]; return u; });
        setIncomingCall(null);
      });
    });

    setSipClient(ua);
    setSipReady(true);
  };

  const reInitSip = (newCreds) => {
    if (sipClient) {
      sipClient.stop();
    }
    localStorage.setItem("sipCredentials", JSON.stringify(newCreds));
    setAccountData(newCreds); // This will trigger initSip via useEffect
  };

  // Fetch SIP credentials via API and set them
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const response = await axios.post(
          `${BASE_URL}/api/get_cust_devices/`,
          {},
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const deviceList = response.data;
        if (deviceList.length > 0) {
          const first = deviceList[0];
          const formatted = '65' + first.serial.replace(/^SG/, '');

          const creds = {
            Display: formatted,
            User: formatted,
            Pass: first.custpbx,
            WSServer: 'wss://pbx.mavistech.cloud:8089/ws',
           // WSServer: 'wss://demo.itcallinfo.info:8089/ws',
            Realm: 'pbx.mavistech.cloud',
           // Realm: 'demo.itcallinfo.info',
          };

          localStorage.setItem("sipCredentials", JSON.stringify(creds));
          setAccountData(creds); // triggers SIP init below
        }
      } catch (err) {
        console.error('Error fetching devices:', err);
      }
    };

    fetchDevices();
  }, [token]);

  // Initialises SIP client when accountData becomes available
  useEffect(() => {
    if (accountData && window.SIP) {
      initSip(accountData);
    }
  }, [accountData]);

  return (
    <SipContext.Provider
      value={{
        incomingCall,
        setIncomingCall,
        sipReady,
        reInitSip,
        audioEnabled,
        safePlayAudio,
        // Shared SIP client and session management
        sipClient,
        sessions,
        activeSession,
        setActiveSession,
        getUniqueID,
      }}
    >
      {children}
    </SipContext.Provider>
  );
};
